import type {
  HighlightSession,
  HighlightEvent,
  HighlightListener,
  HighlightConfig,
  ReviewProgressDetail
} from './types';
import { DEFAULT_CONFIG, REVIEW_PROGRESS_EVENT, LOG_PREFIX } from './constants';

/**
 * Singleton service for managing document highlights across commands.
 * 
 * Features:
 * - Centralized highlight state management
 * - Automatic cleanup with configurable timeouts
 * - Observable lifecycle events
 * - Memory leak prevention
 * - Multiple simultaneous highlights support
 * - Comprehensive error handling
 * 
 * @example
 * ```typescript
 * import { documentHighlightService } from '@/services/documentHighlight';
 * 
 * // Start highlighting
 * documentHighlightService.highlight('cmd-123', ['node-1', 'node-2'], {
 *   commandName: 'detect_ai',
 *   documentId: 'doc-456'
 * });
 * 
 * // Clear when done
 * documentHighlightService.clear('cmd-123');
 * ```
 */
export class DocumentHighlightService {
  private static instance: DocumentHighlightService;
  
  /** Active highlight sessions by command ID */
  private activeHighlights: Map<string, HighlightSession> = new Map();
  
  /** Registered listeners for highlight events */
  private listeners: Set<HighlightListener> = new Set();
  
  /** Service configuration */
  private config: Required<HighlightConfig> = DEFAULT_CONFIG;
  
  private constructor() {
    if (this.config.debug) {
      console.log(`${LOG_PREFIX} Service initialized`);
    }
    
    // Cleanup on page unload to prevent leaks
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.clearAll();
      });
    }
  }
  
  /**
   * Get the singleton instance
   */
  static getInstance(): DocumentHighlightService {
    if (!this.instance) {
      this.instance = new DocumentHighlightService();
    }
    return this.instance;
  }
  
  /**
   * Configure the service
   */
  configure(config: Partial<HighlightConfig>): void {
    this.config = { ...this.config, ...config };
    
    if (this.config.debug) {
      console.log(`${LOG_PREFIX} Configuration updated:`, this.config);
    }
  }
  
  /**
   * Start highlighting document nodes
   * 
   * @param commandId Unique identifier for the command/operation
   * @param nodeIds Array of document node IDs to highlight
   * @param metadata Optional metadata for debugging/monitoring
   */
  highlight(
    commandId: string,
    nodeIds: string[],
    metadata?: HighlightSession['metadata']
  ): void {
    // Validation
    if (!commandId || typeof commandId !== 'string') {
      this.handleError('Invalid commandId', commandId);
      return;
    }
    
    if (!Array.isArray(nodeIds) || nodeIds.length === 0) {
      if (this.config.debug) {
        console.log(`${LOG_PREFIX} Skipping highlight - empty nodeIds`);
      }
      return;
    }
    
    // Check max simultaneous limit
    if (this.activeHighlights.size >= this.config.maxSimultaneous) {
      const message = `Max simultaneous highlights (${this.config.maxSimultaneous}) reached`;
      this.handleError(message, commandId);
      return;
    }
    
    // Clear existing highlight for this command (if any)
    if (this.activeHighlights.has(commandId)) {
      if (this.config.debug) {
        console.log(`${LOG_PREFIX} Clearing previous highlight for ${commandId}`);
      }
      this.clear(commandId);
    }
    
    try {
      // Create session
      const session: HighlightSession = {
        commandId,
        nodeIds,
        startTime: Date.now(),
        metadata
      };
      
      // Store session
      this.activeHighlights.set(commandId, session);
      
      // Setup auto-cleanup if enabled
      if (this.config.autoCleanupTimeout > 0) {
        session.timeoutId = setTimeout(() => {
          if (this.config.debug) {
            console.warn(`${LOG_PREFIX} Auto-cleanup timeout for ${commandId}`);
          }
          this.clear(commandId);
          this.notifyListeners({
            type: 'highlight_timeout',
            commandId,
            nodeIds,
            timestamp: Date.now(),
            metadata
          });
        }, this.config.autoCleanupTimeout);
      }
      
      // Dispatch window event for editor
      this.dispatchWindowEvent('review_progress', nodeIds, commandId);
      
      // Notify listeners
      this.notifyListeners({
        type: 'highlight_started',
        commandId,
        nodeIds,
        timestamp: Date.now(),
        metadata
      });
      
      if (this.config.debug) {
        console.log(`${LOG_PREFIX} Started highlight:`, {
          commandId,
          nodeCount: nodeIds.length,
          metadata
        });
      }
      
    } catch (error) {
      this.handleError('Failed to start highlight', commandId, error as Error);
    }
  }
  
  /**
   * Clear highlighting for a specific command
   * 
   * @param commandId Command identifier to clear
   */
  clear(commandId: string): void {
    const session = this.activeHighlights.get(commandId);
    
    if (!session) {
      if (this.config.debug) {
        console.log(`${LOG_PREFIX} No active highlight for ${commandId}`);
      }
      return;
    }
    
    try {
      // Cancel auto-cleanup timeout
      if (session.timeoutId) {
        clearTimeout(session.timeoutId);
      }
      
      // Remove from active highlights
      this.activeHighlights.delete(commandId);
      
      // Dispatch window event for editor
      this.dispatchWindowEvent('review_complete', [], commandId);
      
      // Notify listeners
      this.notifyListeners({
        type: 'highlight_cleared',
        commandId,
        nodeIds: session.nodeIds,
        timestamp: Date.now(),
        metadata: {
          ...session.metadata,
          duration: Date.now() - session.startTime
        }
      });
      
      if (this.config.debug) {
        console.log(`${LOG_PREFIX} Cleared highlight:`, {
          commandId,
          duration: Date.now() - session.startTime
        });
      }
      
    } catch (error) {
      this.handleError('Failed to clear highlight', commandId, error as Error);
    }
  }
  
  /**
   * Clear all active highlights
   */
  clearAll(): void {
    if (this.config.debug) {
      console.log(`${LOG_PREFIX} Clearing all highlights (${this.activeHighlights.size})`);
    }
    
    const commandIds = Array.from(this.activeHighlights.keys());
    commandIds.forEach(commandId => this.clear(commandId));
  }
  
  /**
   * Check if a command is currently highlighting
   */
  isHighlighting(commandId: string): boolean {
    return this.activeHighlights.has(commandId);
  }
  
  /**
   * Get all active highlight sessions
   */
  getActiveHighlights(): ReadonlyMap<string, HighlightSession> {
    return new Map(this.activeHighlights);
  }
  
  /**
   * Get a specific highlight session
   */
  getHighlightSession(commandId: string): Readonly<HighlightSession> | null {
    const session = this.activeHighlights.get(commandId);
    return session ? { ...session } : null;
  }
  
  /**
   * Subscribe to highlight lifecycle events
   * 
   * @param listener Callback function for events
   * @returns Unsubscribe function
   */
  subscribe(listener: HighlightListener): () => void {
    this.listeners.add(listener);
    
    if (this.config.debug) {
      console.log(`${LOG_PREFIX} Listener subscribed (${this.listeners.size} total)`);
    }
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
      if (this.config.debug) {
        console.log(`${LOG_PREFIX} Listener unsubscribed (${this.listeners.size} remaining)`);
      }
    };
  }
  
  /**
   * Dispatch window event for editor to consume
   */
  private dispatchWindowEvent(
    type: ReviewProgressDetail['type'],
    nodeIds: string[],
    commandId?: string
  ): void {
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      const detail: ReviewProgressDetail = { type, nodeIds, commandId };
      window.dispatchEvent(new CustomEvent(REVIEW_PROGRESS_EVENT, { detail }));
    } catch (error) {
      console.warn(`${LOG_PREFIX} Failed to dispatch window event:`, error);
    }
  }
  
  /**
   * Notify all subscribed listeners
   */
  private notifyListeners(event: HighlightEvent): void {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error(`${LOG_PREFIX} Listener error:`, error);
      }
    });
  }
  
  /**
   * Handle errors consistently
   */
  private handleError(message: string, commandId: string, error?: Error): void {
    const fullMessage = `${LOG_PREFIX} ${message} [${commandId}]`;
    
    if (this.config.throwOnError && error) {
      console.error(fullMessage, error);
      throw error;
    } else {
      console.warn(fullMessage, error);
    }
    
    // Notify listeners of error
    this.notifyListeners({
      type: 'highlight_error',
      commandId,
      nodeIds: [],
      timestamp: Date.now(),
      error
    });
  }
}

// Export singleton instance
export const documentHighlightService = DocumentHighlightService.getInstance();

